import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useSEO, generateWebSiteSchema, generateOrganizationSchema } from '../../utils/seo';

export default function HomePage() {
  const { user, profile } = useAuth();
  const [email, setEmail] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [trendingTopics, setTrendingTopics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // SEO Configuration
  useSEO({
    title: 'Haklı Haksız - Çiftler Arasındaki Tartışmalara Topluluk Karar Veriyor',
    description: 'Eşinizle anlaşamadığınız konularda topluluk gücüyle tarafsız karar alın. Anonim olarak paylaşın, binlerce kullanıcı oy versin. İlişki, finans, ev işleri tartışmalarında haklı mısınız haksız mı öğrenin.',
    keywords: 'çift tartışmaları, ilişki anlaşmazlıkları, topluluk oylaması, anonim tartışma platformu, haklı haksız, evlilik sorunları, çift terapisi alternatifi',
    canonical: '/',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebSiteSchema(),
        generateOrganizationSchema()
      ]
    }
  });

  // Scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch trending topics
  useEffect(() => {
    fetchTrendingTopics();
  }, []);

  const fetchTrendingTopics = async () => {
    try {
      const { data, error } = await supabase
        .from('topics')
        .select(`
          *,
          users!inner(username)
        `)
        .eq('status', 'active')
        .order('votes_right', { ascending: false })
        .limit(4);

      if (error) {
        console.error('Supabase hatası:', error);
        setTrendingTopics([]);
        return;
      }

      if (!data || data.length === 0) {
        setTrendingTopics([]);
        return;
      }

      const formattedTopics = data.map((topic: any) => {
        const totalVotes = (topic.votes_right || 0) + (topic.votes_wrong || 0);
        const rightPercentage = totalVotes > 0 
          ? Math.round((topic.votes_right / totalVotes) * 100)
          : 0;
        const wrongPercentage = totalVotes > 0
          ? Math.round((topic.votes_wrong / totalVotes) * 100)
          : 0;

        return {
          id: topic.id,
          category: topic.category,
          title: topic.title,
          rightVotes: rightPercentage,
          wrongVotes: wrongPercentage,
          totalVotes: totalVotes,
          time: getTimeAgo(topic.created_at),
          categoryColor: getCategoryColor(topic.category),
          endsAt: topic.ends_at
        };
      });

      setTrendingTopics(formattedTopics);
    } catch (error) {
      console.error('Konular yüklenirken hata:', error);
      setTrendingTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'İlişki': 'bg-pink-500',
      'Finans': 'bg-emerald-500',
      'Ev İşleri': 'bg-blue-500',
      'Sosyal Hayat': 'bg-purple-500',
      'Aile': 'bg-orange-500',
      'Diğer': 'bg-gray-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  const getTimeAgo = (date: string) => {
    const now = new Date();
    const past = new Date(date);
    const diffInHours = Math.floor((now.getTime() - past.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Az önce';
    if (diffInHours < 24) return `${diffInHours} saat önce`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} gün önce`;
  };

  const howItWorks = [
    {
      number: 1,
      title: 'Konunu Paylaş',
      description: 'Eşinle aranızdaki anlaşmazlığı anonim olarak paylaş',
      icon: 'ri-edit-line',
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      note: 'Tamamen anonim'
    },
    {
      number: 2,
      title: 'Topluluk Oylasın',
      description: 'Binlerce kullanıcı senin durumun hakkında oy kullansın',
      icon: 'ri-checkbox-multiple-line',
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
      note: 'Tarafsız oylar'
    },
    {
      number: 3,
      title: 'Sonucu Gör',
      description: 'Topluluk kararını öğren ve yorumları oku',
      icon: 'ri-chat-3-line',
      bgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      note: 'Gerçek görüşler'
    }
  ];

  const stats = [
    { number: '12,847', label: 'Aktif Kullanıcı' },
    { number: '2,934', label: 'Çözülen Tartışma' },
    { number: '48,392', label: 'Verilen Oy' },
    { number: '%89', label: 'Memnuniyet Oranı' }
  ];

  const topComments = [
    {
      id: 1,
      rating: 5,
      comment: 'Eşimle aylardır tartıştığımız bir konuyu buraya yazdım. Topluluk görüşü beni çok rahatlattı. Artık kimin haklı olduğunu biliyoruz!',
      user: 'Anonim Kullanıcı #4521',
      likes: 234,
      avatar: 'bg-pink-500'
    },
    {
      id: 2,
      rating: 5,
      comment: 'Harika bir platform! Tarafsız görüşler almak gerçekten çok değerli. Artık tartışmalarımız daha yapıcı geçiyor.',
      user: 'Anonim Kullanıcı #7832',
      likes: 189,
      avatar: 'bg-blue-500'
    },
    {
      id: 3,
      rating: 5,
      comment: 'Başlangıçta şüpheliydim ama gerçekten işe yarıyor. Topluluk çok anlayışlı ve yapıcı yorumlar yapıyor.',
      user: 'Anonim Kullanıcı #2156',
      likes: 156,
      avatar: 'bg-emerald-500'
    }
  ];

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      alert('Aboneliğiniz başarıyla oluşturuldu!');
      setEmail('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/1dcacff907dfbea56e1f04a7f4ffdd79/131f7f20085d959b4f40e787e1603cf3.png" alt="Haklı Haksız Logo" className="h-14 w-14 object-contain" />
              <span className="text-xl font-semibold text-gray-900">haklihaksiz.net</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/votes" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Gündem</Link>
              <Link to="/votes" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Popüler</Link>
              <Link to="/votes" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Yeni Konular</Link>
              <Link to="/about" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Nasıl Çalışır</Link>
            </div>

            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <Link to="/profile" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-user-line mr-1"></i>
                    {profile?.username}
                  </Link>
                  <Link to="/submit" className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">
                    Konu Aç
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/auth/login" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                    Giriş Yap
                  </Link>
                  <Link to="/auth/register" className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">
                    Kayıt Ol
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-6xl lg:text-7xl font-bold text-gray-900 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Haklı mısın,<br />Haksız mı?
                </h1>
                <p className="text-xl text-gray-600 font-light">
                  Çiftler arasındaki tartışmalara topluluk karar veriyor
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/submit" className="bg-black text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-800 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">
                  Hemen Başla
                </Link>
                <Link to="/about" className="bg-white text-black border-2 border-black px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">
                  Nasıl Çalışır?
                </Link>
              </div>

              <p className="text-sm text-gray-500">
                <i className="ri-check-line text-emerald-500 mr-2"></i>
                2,847 tartışma çözüldü
              </p>
            </div>

            {/* Right Visual */}
            <div className="relative h-[500px] flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-300 via-orange-300 to-purple-300 rounded-3xl opacity-30"></div>
              
              {/* Mock Cards */}
              <div className="relative z-10 space-y-4">
                <div className="bg-white p-6 rounded-2xl shadow-xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-3xl font-bold text-emerald-600">68%</span>
                    <span className="text-3xl font-bold text-orange-600">32%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: '68%' }}></div>
                  </div>
                  <p className="mt-4 text-sm text-gray-600">
                    <i className="ri-chat-3-line mr-2"></i>234 yorum
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-xl transform -rotate-3 hover:rotate-0 transition-transform duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-3xl font-bold text-emerald-600">45%</span>
                    <span className="text-3xl font-bold text-orange-600">55%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: '45%' }}></div>
                  </div>
                  <p className="mt-4 text-sm text-gray-600">
                    <i className="ri-chat-3-line mr-2"></i>189 yorum
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trending Topics */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">
                Şu An Tartışılıyor <span className="text-3xl">🔥</span>
              </h2>
              <p className="text-gray-600">Topluluğun en çok oy verdiği güncel tartışmalar</p>
            </div>
            <span className="bg-gray-900 text-white px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap">
              Canlı
            </span>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Konular yükleniyor...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {trendingTopics.map((topic) => (
                <Link 
                  key={topic.id} 
                  to={`/topic/${topic.id}`}
                  className="bg-white border-2 border-gray-100 rounded-3xl p-6 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                >
                  <span className={`${topic.categoryColor} text-white px-3 py-1 rounded-full text-xs font-semibold inline-block mb-4 whitespace-nowrap`}>
                    {topic.category}
                  </span>
                  
                  <h3 className="text-base font-bold text-gray-900 mb-4 line-clamp-3 leading-snug">
                    {topic.title}
                  </h3>

                  <div className="space-y-3 mb-4">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                        style={{ width: `${topic.rightVotes}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-emerald-600">{topic.rightVotes}%</span>
                      <span className="text-2xl font-bold text-orange-600">{topic.wrongVotes}%</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-time-line"></i>
                      {topic.time}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/votes" className="inline-block bg-gray-900 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-800 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">
              Tüm Konuları Gör
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Nasıl Çalışır?
            </h2>
            <p className="text-xl text-gray-600">Üç basit adımda tarafsız karar</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {howItWorks.map((step, index) => (
              <div key={step.number} className="relative">
                <div className="bg-white rounded-3xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <div className={`${step.bgColor} w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-6`}>
                    <i className={`${step.icon} ${step.iconColor} text-5xl`}></i>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {step.number}. {step.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {step.description}
                  </p>
                  
                  <span className="text-xs text-gray-400">{step.note}</span>
                </div>

                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-3xl text-gray-300"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl font-light text-white mb-16" style={{ fontFamily: 'Playfair Display, serif' }}>
            Topluluk Gücü
          </h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-white mb-3">
                  {stat.number}
                </div>
                <div className="text-gray-400 text-sm">
                  {stat.label}
                </div>
                {index < stats.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-px h-16 bg-gray-700 transform -translate-y-1/2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Comments */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Left - Comments */}
            <div className="lg:col-span-3 space-y-8">
              <div className="mb-12">
                <h2 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Topluluk Ne Diyor?
                </h2>
                <p className="text-xl text-gray-600">Gerçek kullanıcılardan gerçek yorumlar</p>
              </div>

              <div className="space-y-6">
                {topComments.map((comment) => (
                  <div key={comment.id} className="bg-white border-2 border-gray-100 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(comment.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-orange-500 text-lg"></i>
                      ))}
                    </div>

                    <p className="text-gray-700 text-lg mb-6 leading-relaxed italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                      "{comment.comment}"
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${comment.avatar} rounded-full flex items-center justify-center`}>
                          <i className="ri-user-line text-white text-lg"></i>
                        </div>
                        <span className="text-sm text-gray-600">{comment.user}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <i className="ri-heart-line"></i>
                        <span className="text-sm">{comment.likes}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Images */}
            <div className="lg:col-span-2 space-y-4">
              <div className="h-64 rounded-2xl overflow-hidden">
                <img 
                  src="https://public.readdy.ai/ai/img_res/c41abae6f91215dd1d646f71b1a625e7.jpg" 
                  alt="Mutlu Çift" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="h-64 rounded-2xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20illustration%20of%20two%20people%20having%20calm%20discussion%20with%20speech%20bubbles%20and%20voting%20symbols%20in%20minimalist%20style%20with%20soft%20pastel%20colors%20representing%20peaceful%20conflict%20resolution&width=400&height=300&seq=discussion1&orientation=landscape" 
                  alt="Tartışma İllüstrasyonu" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 bg-gradient-to-br from-pink-400 via-orange-400 to-purple-500">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <h2 className="text-6xl font-bold text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Senin Tartışman<br />Nedir?
            </h2>
            
            <div className="text-right space-y-6">
              <p className="text-xl text-white font-light max-w-md">
                Topluluğun görüşünü al,<br />tarafsız karar ver
              </p>
              <Link 
                to="/submit" 
                className="inline-flex items-center gap-3 bg-white text-black px-8 py-5 rounded-full text-lg font-semibold hover:shadow-2xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Konunu Paylaş
                <span className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white"></i>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 mx-6 my-6 rounded-3xl px-12 py-16">
        <div className="max-w-7xl mx-auto">
          {/* Top Section */}
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Newsletter */}
            <div className="space-y-4">
              <h3 className="text-4xl font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Güncel Kal
              </h3>
              <p className="text-gray-400 mb-6">Yeni tartışmalardan haberdar ol</p>
              
              <form onSubmit={handleSubscribe} className="space-y-4">
                <input 
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-posta adresin"
                  className="w-full bg-transparent border-b-2 border-gray-600 text-white placeholder-gray-500 py-3 focus:outline-none focus:border-white transition-colors"
                  required
                />
                <button 
                  type="submit"
                  className="bg-white text-black px-6 py-3 rounded-full text-sm font-semibold hover:bg-gray-200 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  Abone Ol
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-white text-lg font-semibold mb-6">Keşfet</h4>
              <div className="space-y-3">
                <Link to="/votes" className="block text-gray-400 hover:text-white transition-colors underline whitespace-nowrap cursor-pointer">Gündem</Link>
                <Link to="/votes" className="block text-gray-400 hover:text-white transition-colors underline whitespace-nowrap cursor-pointer">Popüler</Link>
                <Link to="/votes" className="block text-gray-400 hover:text-white transition-colors underline whitespace-nowrap cursor-pointer">Kategoriler</Link>
                <Link to="/votes" className="block text-gray-400 hover:text-white transition-colors underline whitespace-nowrap cursor-pointer">İstatistikler</Link>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white text-lg font-semibold mb-6">İletişim</h4>
              <div className="space-y-3">
                <a href="mailto:info@haklihaksiz.net" className="block text-gray-400 hover:text-white transition-colors whitespace-nowrap">
                  info@haklihaksiz.net
                </a>
                <div className="pt-4">
                  <p className="text-gray-400 mb-3">Bizi Takip Et</p>
                  <div className="flex gap-4">
                    <a href="#" className="text-gray-400 hover:text-white transition-colors underline whitespace-nowrap cursor-pointer">Instagram</a>
                    <a href="#" className="text-gray-400 hover:text-white transition-colors underline whitespace-nowrap cursor-pointer">Twitter</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex gap-6">
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm underline whitespace-nowrap cursor-pointer">Instagram</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm underline whitespace-nowrap cursor-pointer">Twitter</a>
              </div>
              
              <p className="text-gray-400 text-sm whitespace-nowrap">© 2025 haklıhaksız.net</p>
              
              <div className="flex gap-6">
                <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm underline whitespace-nowrap cursor-pointer">Gizlilik</Link>
                <Link to="/terms" className="text-gray-400 hover:text-white transition-colors text-sm underline whitespace-nowrap cursor-pointer">Kullanım Şartları</Link>
              </div>
            </div>
            
            <div className="text-center mt-6">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400 transition-colors text-xs underline whitespace-nowrap cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
